import { Loader2, Sparkles, Code } from "lucide-react";
import { useEffect, useState } from "react";

export const LoadingFallback = () => {
  const [showSlowMessage, setShowSlowMessage] = useState(false);
  const [loadingText, setLoadingText] = useState("Loading");

  // Show helpful message if loading takes too long
  useEffect(() => {
    const slowTimer = setTimeout(() => {
      setShowSlowMessage(true);
    }, 3000); // Show after 3 seconds

    return () => clearTimeout(slowTimer);
  }, []);

  // Animate loading text
  useEffect(() => {
    const messages = [
      "Loading",
      "Preparing your workspace",
      "Almost there",
      "Setting things up"
    ];
    let index = 0;

    const interval = setInterval(() => {
      index = (index + 1) % messages.length;
      setLoadingText(messages[index]);
    }, 1500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20"
      role="alert"
      aria-live="polite"
      aria-label="Loading content"
    >
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-theme-gradient opacity-10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      {/* Main Loading Content */}
      <div className="relative flex flex-col items-center justify-center gap-6 p-8">
        {/* Logo with Spinner */}
        <div className="relative">
          {/* Outer rotating ring */}
          <div 
            className="absolute inset-0 rounded-full animate-spin"
            style={{ 
              background: `conic-gradient(from 0deg, transparent, rgb(var(--theme-primary)), transparent)`,
              filter: 'blur(8px)',
              animationDuration: '3s'
            }}
          />
          
          {/* Logo Container */}
          <div className="relative">
            <div 
              className="absolute inset-0 rounded-2xl opacity-20 blur-lg"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
              }}
            />
            <div 
              className="relative flex h-20 w-20 items-center justify-center rounded-2xl shadow-2xl"
              style={{
                background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
              }}
            >
              <Code className="h-10 w-10 text-white animate-pulse" />
            </div>
          </div>
        </div>

        {/* Spinner with gradient */}
        <div className="relative">
          <Loader2 
            className="h-12 w-12 animate-spin"
            style={{ color: `rgb(var(--theme-primary))` }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <div 
              className="h-8 w-8 rounded-full opacity-20 blur-md"
              style={{ background: `rgb(var(--theme-primary))` }}
            />
          </div>
        </div>

        {/* Loading Text with Animation */}
        <div className="flex flex-col items-center gap-2">
          <p 
            className="text-lg font-semibold animate-pulse"
            style={{
              background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}
          >
            {loadingText}
            <span className="inline-flex ml-1">
              <span className="animate-bounce delay-0">.</span>
              <span className="animate-bounce delay-100">.</span>
              <span className="animate-bounce delay-200">.</span>
            </span>
          </p>
          
          {/* Slow loading message */}
          {showSlowMessage && (
            <p className="text-sm text-muted-foreground animate-in fade-in slide-in-from-bottom duration-500 flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Taking a bit longer than usual...
            </p>
          )}
        </div>

        {/* Progress Bar (optional) */}
        <div className="w-48 h-1 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full rounded-full animate-progress"
            style={{
              background: `linear-gradient(90deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
              animation: 'progress 2s ease-in-out infinite'
            }}
          />
        </div>
      </div>

      {/* Add this to your global CSS */}
      <style>{`
        @keyframes progress {
          0% { width: 0%; margin-left: 0%; }
          50% { width: 60%; margin-left: 20%; }
          100% { width: 0%; margin-left: 100%; }
        }
        .delay-0 { animation-delay: 0ms; }
        .delay-100 { animation-delay: 100ms; }
        .delay-200 { animation-delay: 200ms; }
        .delay-1000 { animation-delay: 1000ms; }
      `}</style>
    </div>
  );
};
